import type { SavedDataTableView } from 'src/features/data-table-views';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';
import { useSavedDataTableViews } from 'src/features/data-table-views';

import type { ExportFormat } from '@savvy/data-table';
import { EXPORT_FORMATS } from '@savvy/data-table';

export type SavedExportConfig = {
  columnIds: string[];
  format: ExportFormat;
  applyDashboardFilters: boolean;
};

export type SavedExportPreset = SavedDataTableView<SavedExportConfig>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

const EXPORT_FORMAT_VALUES: readonly string[] = EXPORT_FORMATS;

function isExportFormat(value: unknown): value is ExportFormat {
  return typeof value === 'string' && EXPORT_FORMAT_VALUES.includes(value);
}

function isSavedExportConfig(value: unknown): value is SavedExportConfig {
  if (!isRecord(value)) return false;
  return (
    isStringArray(value['columnIds']) &&
    isExportFormat(value['format']) &&
    typeof value['applyDashboardFilters'] === 'boolean'
  );
}

export type UseSavedHouseholdExportPresetsReturn = {
  savedPresets: SavedExportPreset[];
  atLimit: boolean;
  savePreset: (name: string, config: SavedExportConfig) => SavedExportPreset | null;
  renamePreset: (id: string, name: string) => void;
  deletePreset: (id: string) => SavedExportPreset[];
};

export function useSavedHouseholdExportPresets(): UseSavedHouseholdExportPresetsReturn {
  const { savedViews, atLimit, saveView, renameView, deleteView } = useSavedDataTableViews<SavedExportConfig>({
    storageKey: LOCAL_STORAGE_KEYS.HOUSEHOLD_EXPORT_SAVED_PRESETS,
    filtersValidator: isSavedExportConfig,
  });

  return {
    savedPresets: savedViews,
    atLimit,
    savePreset: saveView,
    renamePreset: renameView,
    deletePreset: deleteView,
  };
}
